import { Box, Button, Link, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useState } from 'react';
import { useCreateAccount } from './hooks/useCreateAccount';
import { useResendRegistrationCode } from './hooks/useResendRegistrationCode';
import { SecuritySkeleton } from '../common/SecuritySkeleton';
import { OtpField } from '../common/OtpField';
import { RegisterErrorMessage } from './RegisterErrorMessage';
import { useRegister, useRegisterError } from './reducer/useRegister';
import { contactLink } from '../common/ContactLink';
import { useResetRegisterState } from './hooks/useResetRegisterState';
import { LoadingIndicator } from '@xolv-mybrightlink/common';

export const RegisterOtpInput = () => {
  const createAccount = useCreateAccount();
  const resendRegistrationCode = useResendRegistrationCode();
  const resetRegisterState = useResetRegisterState();
  const registerState = useRegister();
  const registerError = useRegisterError();

  const [otp, setOtp] = useState('');
  const [didResendOtp, setDidResendOtp] = useState(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await createAccount(otp);
    },
    [otp, createAccount],
  );

  const triggerResendRegistrationCode = useCallback(async () => {
    setOtp('');
    setDidResendOtp(true);
    await resendRegistrationCode();
  }, [resendRegistrationCode]);

  return (
    <SecuritySkeleton
      onBackPressed={() => resetRegisterState('REQUESTING_REGISTRATION_CODE')}
    >
      <Box
        component='form'
        noValidate
        onSubmit={handleSubmit}
        sx={{
          mt: 15,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <Typography
          variant='body1'
          sx={{
            color: '#626a7a',
            fontWeight: 500,
          }}
        >
          <FormattedMessage id='signup.zip.input.success.label1' />
        </Typography>

        <Typography
          variant='body2'
          sx={{
            color: '#626a7a',
            mt: 5,
          }}
        >
          <FormattedMessage id='signup.zip.input.success.label2' />
        </Typography>
        <OtpField otp={otp} setOtp={setOtp} dataTestid='otpInput' />
        <Button
          type='submit'
          fullWidth
          variant='contained'
          size='large'
          data-testid='submitOtpButton'
          sx={{
            mt: 6,
            mb: 4,
            borderRadius: 30,
            py: 3.5,
          }}
          disabled={otp.length != 6}
          disableElevation
        >
          <FormattedMessage id='signup.button.label' />
        </Button>
      </Box>
      {registerError && <RegisterErrorMessage errorCode={registerError} />}
      <Typography
        variant='body2'
        sx={{
          color: '#626a7a',
          mt: 4,
        }}
      >
        <FormattedMessage
          id='login.otp.button.resend.label'
          values={{
            resend: (text) => (
              <Link
                data-testid='resendOtpButton'
                component='button'
                variant='body2'
                onClick={triggerResendRegistrationCode}
                sx={{
                  fontWeight: 500,
                }}
              >
                {text}
              </Link>
            ),
          }}
        />
      </Typography>
      {didResendOtp && !registerError && (
        <Box
          data-testid='resendMessage'
          sx={{
            mt: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant='body1' sx={{ textAlign: 'center' }}>
            <FormattedMessage
              id='login.otp.button.resend.success.label'
              values={{ contactLink }}
            />
          </Typography>
        </Box>
      )}
      {registerState.loading && <LoadingIndicator fullHeight overlay />}
    </SecuritySkeleton>
  );
};
